// ExportableInsightCard - 1:1 aspect ratio card optimised for sharing
// Shows: HiPPO Band, 2-3 signals, simplified schematic
// Does NOT show: prices, percentages, ROI, time claims, postcode

import { forwardRef } from "react";
import { Sparkles, Home, Maximize2, TrendingUp, Building2 } from "lucide-react";

// Band configurations (qualitative only) - 5 band system
const BAND_CONFIG = {
  "Negligible": { color: "bg-gray-500", textColor: "text-gray-400", label: "Negligible Potential" },
  "Low": { color: "bg-amber-500/80", textColor: "text-amber-400", label: "Limited Potential" },
  "Medium": { color: "bg-cipher-gold", textColor: "text-cipher-gold", label: "Notable Potential" },
  "High": { color: "bg-cipher-green", textColor: "text-cipher-green", label: "Strong Potential" },
  "Exceptional": { color: "bg-emerald-500", textColor: "text-emerald-400", label: "Exceptional Potential" }
};

// Signal icons by category
const SIGNAL_ICONS = {
  spatial: Maximize2,
  unlock: Building2,
  market: TrendingUp,
  default: Sparkles
};

// Simplified property schematic for card
function SimpleSchematic({ dwellingType }) {
  return (
    <div className="w-full h-16 flex items-center justify-center text-white/20">
      <svg viewBox="0 0 100 60" className="h-full w-auto">
        {dwellingType === "flat" ? (
          <rect x="20" y="10" width="60" height="45" fill="none" stroke="currentColor" strokeWidth="2" rx="2" />
        ) : dwellingType === "bungalow" ? (
          <>
            <rect x="15" y="30" width="70" height="25" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M10 30 L50 10 L90 30" fill="none" stroke="currentColor" strokeWidth="2" />
          </>
        ) : (
          <>
            <rect x="20" y="30" width="60" height="25" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M15 30 L50 10 L85 30" fill="none" stroke="currentColor" strokeWidth="2" />
          </>
        )}
      </svg>
    </div>
  );
}

// Main ExportableInsightCard component - forwardRef for html2canvas
const ExportableInsightCard = forwardRef(function ExportableInsightCard({ 
  band, 
  signals = [], 
  dwellingType,
  firstName 
}, ref) {
  const bandConfig = BAND_CONFIG[band] || BAND_CONFIG["Medium"];
  
  // Get top 2-3 signals only (directional language)
  const topSignals = signals.slice(0, 3);
  
  // Get current month/year
  const now = new Date();
  const timestamp = now.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  
  return (
    <div 
      ref={ref}
      className="w-[400px] h-[400px] bg-gradient-to-br from-cipher-charcoal via-cipher-charcoal to-black p-6 flex flex-col rounded-2xl"
      style={{ aspectRatio: '1/1' }}
      data-testid="exportable-insight-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-white text-sm">HiPPO</span>
        </div>
        <Sparkles className="w-5 h-5 text-cipher-gold" />
      </div>
      
      {/* HiPPO Band - Main Focus */}
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        {/* Band Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-3`}>
          <div className={`w-2.5 h-2.5 rounded-full ${bandConfig.color}`} />
          <span className={`text-sm font-semibold ${bandConfig.textColor}`}>{bandConfig.label}</span>
        </div>
        
        {/* Name */}
        {firstName && (
          <p className="text-white/60 text-sm mb-3">{firstName}&apos;s Property Insight</p>
        )}
        
        {/* Simplified Schematic */}
        <SimpleSchematic dwellingType={dwellingType} />
        
        {/* Top Signals - Directional only */}
        <div className="mt-4 space-y-2 w-full max-w-[280px]">
          {topSignals.map((signal, idx) => {
            const Icon = SIGNAL_ICONS[signal.category] || SIGNAL_ICONS.default;
            return (
              <div 
                key={idx} 
                className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10"
              >
                <Icon className="w-4 h-4 text-cipher-green flex-shrink-0" />
                <span className="text-xs text-white/80 truncate">{signal.short_copy}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <span className="text-[10px] text-white/30">Generated by HiPPO</span>
        <span className="text-[10px] text-white/30">{timestamp}</span>
      </div>
    </div>
  );
});

export default ExportableInsightCard;
